import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, CheckCircle, Users, Settings, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Implementation = () => {
  const navigate = useNavigate();

  const implementationSteps = [
    {
      step: '1',
      title: 'Discovery & Planning',
      description: 'Comprehensive assessment of your current processes and requirements',
      duration: '1-2 weeks'
    },
    {
      step: '2', 
      title: 'System Configuration',
      description: 'Custom setup of templates, workflows, and user roles specific to your operation',
      duration: '2-3 weeks'
    },
    {
      step: '3',
      title: 'Data Migration',
      description: 'Secure transfer of existing data and integration with current systems',
      duration: '1-2 weeks'
    },
    {
      step: '4',
      title: 'Training & Go-Live',
      description: 'Comprehensive staff training and supervised launch across all locations',
      duration: '2-4 weeks'
    }
  ];

  const services = [
    'Custom checklist template development',
    'User role and permission configuration',
    'Third-party system integrations',
    'Data migration and validation',
    'Staff training programs',
    'Go-live support and monitoring'
  ];

  return (
    <div className="min-h-screen">
      <header className="glass-panel-nav">
        <div className="container mx-auto px-4 py-4">
          <Button 
            variant="glass" 
            onClick={() => navigate('/')}
            className="mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Button>
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 rounded-xl bg-crystal-orange/10 border border-crystal-orange/20">
              <Building2 className="h-8 w-8 text-crystal-orange" />
            </div>
            <div>
              <h1 className="heading-hero">Implementation & Setup</h1>
              <p className="subheading mt-2">
                Complete platform deployment with custom configuration for your operation
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="py-16">
        <div className="container mx-auto px-4">
          {/* Implementation Process */}
          <section className="mb-16">
            <h2 className="heading-section mb-8 text-center">Implementation Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {implementationSteps.map((step, index) => (
                <Card key={step.step} className="glass-panel border-0 bg-transparent">
                  <CardHeader className="text-center">
                    <div className="w-12 h-12 rounded-full bg-crystal-orange text-white font-bold text-xl flex items-center justify-center mx-auto mb-4">
                      {step.step}
                    </div>
                    <CardTitle className="text-white text-lg">{step.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-white/70 mb-4">{step.description}</p>
                    <div className="text-crystal-orange font-semibold">{step.duration}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Services Included */}
          <section className="mb-16">
            <div className="glass-panel-elevated p-8">
              <h2 className="heading-section mb-8 text-center">What's Included</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-4">
                  {services.map((service, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-crystal-orange mt-1 flex-shrink-0" />
                      <span className="text-white">{service}</span>
                    </div>
                  ))}
                </div>
                <div className="text-center">
                  <div className="glass-panel p-8">
                    <Users className="h-12 w-12 text-crystal-orange mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-4">Dedicated Implementation Team</h3>
                    <p className="text-white/70">
                      Your dedicated implementation specialist will guide you through every step of the process.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="text-center">
            <div className="glass-panel-elevated p-8">
              <h3 className="text-2xl font-bold text-white mb-4">
                Ready to Get Started?
              </h3>
              <p className="text-white/70 mb-6 max-w-2xl mx-auto">
                Our implementation experts are ready to help you deploy Crystal HACCP across your operation with minimal disruption.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="cta-primary px-8 py-3">
                  Schedule Implementation Call
                </Button>
                <Button className="cta-secondary px-8 py-3">
                  Download Implementation Guide
                </Button>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default Implementation;